import { useState } from 'react';
import { isAxiosError } from 'axios';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { qrMatrix } from '@/components/receipts/qr-code';
import { useHasPermission } from '@/auth/auth-store';
import {
  useCashierLoginQr,
  useGenerateCashierLoginQr,
  type CashierLoginQrIssued,
} from '@/hooks/use-cashier-login-qr';
import type { Cashier } from '@/hooks/use-employees';
import { useT } from '@/i18n/use-t';

/**
 * VL-349 — "Login QR code" for one cashier: generate or regenerate it, then
 * show it and offer it as a PNG.
 *
 * The code is shown once, from the generate response, the way a new PIN is:
 * Back Office keeps only its hash, so the dialog cannot show an existing code
 * again — the way to a new image is to regenerate, which also retires the old
 * code on every till at its next sync.
 *
 * Shown only to holders of `employees.admin` (the server enforces it; this is
 * the matching UX), and for imported cashiers too: issuing a code is not an
 * edit of the cashier.
 */
export function CashierLoginQrButton({
  cashier,
  size = 'sm',
}: {
  cashier: Pick<Cashier, 'id' | 'displayName' | 'employeeCode' | 'status'>;
  size?: 'sm' | 'default';
}) {
  const t = useT();
  const allowed = useHasPermission('employees.admin');
  const [open, setOpen] = useState(false);
  if (!allowed) return null;
  return (
    <>
      <Button type="button" variant="outline" size={size} onClick={() => setOpen(true)}>
        {t('employees.loginQr.action', 'Login QR code')}
      </Button>
      {open && <CashierLoginQrDialog cashier={cashier} onClose={() => setOpen(false)} />}
    </>
  );
}

function CashierLoginQrDialog({
  cashier,
  onClose,
}: {
  cashier: Pick<Cashier, 'id' | 'displayName' | 'employeeCode' | 'status'>;
  onClose: () => void;
}) {
  const t = useT();
  const status = useCashierLoginQr(cashier.id);
  const generate = useGenerateCashierLoginQr();
  const [issued, setIssued] = useState<CashierLoginQrIssued | null>(null);
  const [error, setError] = useState<string | null>(null);
  const label = `${cashier.displayName} (${cashier.employeeCode})`;
  const hasCode = status.data?.active === true;

  async function handleGenerate(): Promise<void> {
    setError(null);
    try {
      setIssued(await generate.mutateAsync(cashier.id));
    } catch (err) {
      setError(extractError(err, t));
    }
  }

  return (
    <AlertDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('employees.loginQr.title', 'Login QR code for {name}').replace('{name}', label)}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {issued !== null
              ? t(
                  'employees.loginQr.issuedBody',
                  'Scanning this code on a till signs the cashier in without a PIN. It is shown only now: download it and hand it to the cashier. It stays valid until it is regenerated.',
                )
              : hasCode
                ? t(
                    'employees.loginQr.activeBody',
                    'This cashier has a valid login QR code (version {version}). It cannot be shown again. Regenerating issues a new code; the current one stops working on each till after its next sync.',
                  ).replace('{version}', String(status.data?.version ?? ''))
                : t(
                    'employees.loginQr.noneBody',
                    'This cashier has no login QR code. Scanning the code on a till signs the cashier in without a PIN, with the same rights and store as a typed sign-in.',
                  )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {issued !== null && <IssuedCode issued={issued} label={label} />}

        {issued === null && cashier.status !== 'active' && (
          <p className="text-sm text-muted-foreground">
            {t(
              'employees.loginQr.suspended',
              'The cashier is suspended. Reactivate the cashier to issue a login QR code.',
            )}
          </p>
        )}
        {error !== null && <p className="text-sm text-destructive">{error}</p>}

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>
            {issued !== null ? t('employees.loginQr.done', 'Done') : t('common.cancel', 'Cancel')}
          </AlertDialogCancel>
          {issued === null && (
            <AlertDialogAction
              disabled={generate.isPending || status.isLoading || cashier.status !== 'active'}
              onClick={(event) => {
                // Keep the dialog open to show the new code.
                event.preventDefault();
                void handleGenerate();
              }}
            >
              {hasCode
                ? t('employees.loginQr.regenerate', 'Regenerate')
                : t('employees.loginQr.generate', 'Generate')}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Pixels per QR module in the downloaded PNG. */
const PNG_MODULE_PX = 10;

function IssuedCode({ issued, label }: { issued: CashierLoginQrIssued; label: string }) {
  const t = useT();
  const matrix = qrMatrix(issued.payload);
  if (matrix === null) return null;
  return (
    <div className="my-2 flex flex-col items-center gap-3" data-testid="login-qr-issued">
      <svg
        role="img"
        aria-label={t('employees.loginQr.imageLabel', 'Login QR code for {name}').replace(
          '{name}',
          label,
        )}
        viewBox={`0 0 ${matrix.size} ${matrix.size}`}
        shapeRendering="crispEdges"
        className="h-56 w-56 rounded-sm bg-white"
      >
        <path d={matrix.path} className="fill-black" />
      </svg>
      <p className="text-xs text-muted-foreground">
        {t('employees.loginQr.version', 'Version {version}').replace(
          '{version}',
          String(issued.version),
        )}
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => downloadPng(issued.payload, fileName(label, issued.version))}
      >
        {t('employees.loginQr.download', 'Download PNG')}
      </Button>
    </div>
  );
}

/**
 * Draw the code on a canvas and save it as a PNG. Black modules on white,
 * the quiet zone included (it is part of the matrix), whole pixels per
 * module so a printer does not blur the edges.
 */
export function renderLoginQrPng(payload: string, doc: Document = document): string | null {
  const matrix = qrMatrix(payload);
  if (matrix === null) return null;
  const canvas = doc.createElement('canvas');
  canvas.width = matrix.size * PNG_MODULE_PX;
  canvas.height = matrix.size * PNG_MODULE_PX;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return null;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';
  ctx.scale(PNG_MODULE_PX, PNG_MODULE_PX);
  ctx.fill(new Path2D(matrix.path));
  return canvas.toDataURL('image/png');
}

function downloadPng(payload: string, name: string): void {
  const url = renderLoginQrPng(payload);
  if (url === null) return;
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/** `login-qr-<employee code>-v<version>.png`, safe for any file system. */
export function fileName(label: string, version: number): string {
  const code = /\(([^)]*)\)\s*$/.exec(label)?.[1] ?? 'cashier';
  return `login-qr-${code.replace(/[^A-Za-z0-9_-]+/g, '_')}-v${version}.png`;
}

type Translate = (key: string, bakedEnglish?: string) => string;

function extractError(err: unknown, t: Translate): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { message?: string | string[] } | undefined;
    if (typeof data?.message === 'string') return data.message;
    if (Array.isArray(data?.message)) return data.message.join('; ');
    return err.message;
  }
  return t('employees.error.unexpected', 'Unexpected error');
}
